import React, { useState } from "react";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Alert, Typography, Box, Checkbox, FormControlLabel, Divider, Link,
} from "@mui/material";
import {
  FOCUS_COMPANIES, FocusPushResult, FocusPushItem,
  fetchFocusPushItems, pushOrderToFocus, pushQuotationToFocus,
} from "../service/focus.service";

type PushType = "order" | "quote";

interface Props {
  type: PushType;
  id: number;
  /** if the order already has a Focus voucher, pass it to show the pushed state */
  existingVoucherNo?: string | null;
  /** notify parent (e.g. to refresh the row) after a successful live push */
  onPushed?: (voucherNo: string) => void;
}

type Stage = "idle" | "choose" | "loadingItems" | "items" | "pushing" | "done" | "error";

/**
 * Push-to-Focus action. Flow:
 *   button -> company picker -> Next -> item picker (all lines checked by default; untick the
 *   ones you don't want) -> Create Sales Order -> confirmation (SO number, date, customer, value).
 * Only the ticked line items are sent (by their backend line index) and posted to Focus.
 * Idempotent: if a voucher already exists, the button is disabled and shows the voucher number.
 */
const PushToFocusButton: React.FC<Props> = ({ type, id, existingVoucherNo, onPushed }) => {
  const [stage, setStage] = useState<Stage>("idle");
  const [company, setCompany] = useState<number>(FOCUS_COMPANIES[0].id);
  const [items, setItems] = useState<FocusPushItem[]>([]);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [result, setResult] = useState<FocusPushResult | null>(null);
  const [error, setError] = useState<string>("");

  const pushed = !!existingVoucherNo || (result && !result.dryRun && !!result.voucherNo);

  const selectedCount = items.reduce((n, it) => (selected[it.idx] ? n + 1 : n), 0);
  const allChecked = items.length > 0 && selectedCount === items.length;

  // Step 1 -> 2: load the order's line items for the picker.
  const loadItems = async () => {
    setStage("loadingItems");
    setError("");
    try {
      const res = await fetchFocusPushItems(type, id);
      if (res && res.status === 100 && Array.isArray(res.data)) {
        const its = res.data as FocusPushItem[];
        const sel: Record<number, boolean> = {};
        its.forEach((it) => { sel[it.idx] = true; });   // everything selected by default
        setItems(its);
        setSelected(sel);
        setStage("items");
      } else {
        setError((res && res.msg) || "Could not load the order's line items.");
        setStage("error");
      }
    } catch (e: any) {
      setError(e?.message || "Network error while loading line items.");
      setStage("error");
    }
  };

  const toggle = (idx: number) => setSelected((s) => ({ ...s, [idx]: !s[idx] }));
  const setAll = (val: boolean) => {
    const sel: Record<number, boolean> = {};
    items.forEach((it) => { sel[it.idx] = val; });
    setSelected(sel);
  };

  // Step 2 -> done: post only the ticked lines.
  const submit = async () => {
    const chosen = items.filter((it) => selected[it.idx]).map((it) => it.idx);
    if (chosen.length === 0) return;
    setStage("pushing");
    setError("");
    try {
      const fn = type === "order" ? pushOrderToFocus : pushQuotationToFocus;
      // LIVE: posts to Focus. The backend resolves the per-company CustomerAC + Shipping Master
      // from the order's dealer code automatically, and builds the SO body from `chosen` only.
      const res = await fn(id, company, false, chosen);
      if (res && res.status === 100 && res.data) {
        const data = res.data as FocusPushResult;
        setResult(data);
        setStage("done");
        if (!data.dryRun && data.voucherNo && onPushed) onPushed(data.voucherNo);
      } else {
        setError((res && res.msg) || "Push failed.");
        setStage("error");
      }
    } catch (e: any) {
      setError(e?.message || "Network error while pushing to Focus.");
      setStage("error");
    }
  };

  const close = () => setStage("idle");

  // Show the badge once pushed — but NOT while the success dialog is open, so a fresh
  // live push displays its confirmation (SO no., date, value) before collapsing to the badge.
  if (pushed && stage !== "done") {
    const vno = existingVoucherNo || result?.voucherNo;
    return (
      <Button size="small" color="success" variant="outlined" disabled>
        Pushed · {vno}
      </Button>
    );
  }

  return (
    <>
      <Button size="small" variant="contained" onClick={() => setStage("choose")}>
        Push to Focus
      </Button>

      {/* Step 1 — company picker */}
      <Dialog open={stage === "choose" || stage === "loadingItems"} onClose={stage === "loadingItems" ? undefined : close}>
        <DialogTitle>Push {type === "order" ? "Order" : "Quotation"} #{id} to Focus</DialogTitle>
        <DialogContent sx={{ pt: 1, minWidth: 320 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="focus-company-label">Company</InputLabel>
            <Select
              labelId="focus-company-label"
              label="Company"
              value={company}
              disabled={stage === "loadingItems"}
              onChange={(e) => setCompany(Number(e.target.value))}
            >
              {FOCUS_COMPANIES.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {stage === "loadingItems" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={18} /> <Typography variant="body2">Loading line items…</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={stage === "loadingItems"}>Cancel</Button>
          <Button onClick={loadItems} variant="contained" disabled={stage === "loadingItems"}>Next: choose items</Button>
        </DialogActions>
      </Dialog>

      {/* Step 2 — item picker (all ticked by default; untick to exclude) */}
      <Dialog open={stage === "items" || stage === "pushing"} onClose={stage === "pushing" ? undefined : close} maxWidth="sm" fullWidth>
        <DialogTitle>Select items for Sales Order</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedCount} of {items.length} item{items.length === 1 ? "" : "s"} selected
            </Typography>
            <Link
              component="button"
              type="button"
              underline="hover"
              variant="body2"
              onClick={() => setAll(!allChecked)}
              sx={{ pointerEvents: stage === "pushing" ? "none" : "auto", opacity: stage === "pushing" ? 0.5 : 1 }}
            >
              {allChecked ? "Unselect all" : "Select all"}
            </Link>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 320, overflowY: "auto", mt: 1 }}>
            {items.map((it) => (
              <Box key={it.idx} sx={{ display: "flex", alignItems: "flex-start", py: 0.25 }}>
                <FormControlLabel
                  sx={{ alignItems: "flex-start", m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!selected[it.idx]}
                      disabled={stage === "pushing"}
                      onChange={() => toggle(it.idx)}
                      sx={{ pt: 0.25 }}
                    />
                  }
                  label={
                    <Box sx={{ ml: 0.5 }}>
                      <Typography variant="body2">
                        <b>{it.code || "—"}</b> &nbsp;×&nbsp; {it.qty}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{it.desc}</Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No line items found on this order.
              </Typography>
            )}
          </Box>
          {stage === "pushing" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={18} /> <Typography variant="body2">Creating sales order in Focus…</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStage("choose")} disabled={stage === "pushing"}>Back</Button>
          <Button onClick={submit} variant="contained" disabled={stage === "pushing" || selectedCount === 0}>
            Create Sales Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation */}
      <Dialog open={stage === "done"} onClose={close}>
        <DialogTitle>{result?.dryRun ? "Dry run — payload built" : "Sales Order created"}</DialogTitle>
        <DialogContent sx={{ minWidth: 340 }}>
          {result && !result.dryRun && (
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.75, columnGap: 2 }}>
              <Typography variant="body2" color="text.secondary">SO Number</Typography>
              <Typography variant="body2"><b>{result.voucherNo}</b></Typography>
              <Typography variant="body2" color="text.secondary">Date</Typography>
              <Typography variant="body2">{result.date}</Typography>
              <Typography variant="body2" color="text.secondary">Customer</Typography>
              <Typography variant="body2">{result.customer}</Typography>
              <Typography variant="body2" color="text.secondary">Value</Typography>
              <Typography variant="body2">₹ {Number(result.value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</Typography>
            </Box>
          )}
          {result?.dryRun && (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.75, columnGap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Customer</Typography>
                <Typography variant="body2">{result.customer}</Typography>
                <Typography variant="body2" color="text.secondary">Value</Typography>
                <Typography variant="body2">₹ {Number(result.value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</Typography>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography variant="body2">{result.date}</Typography>
                <Typography variant="body2" color="text.secondary">Line items</Typography>
                <Typography variant="body2">
                  {(() => {
                    try {
                      const body = (result.payload as any)?.data?.[0]?.Body;
                      return Array.isArray(body) ? body.length : "—";
                    } catch { return "—"; }
                  })()}
                </Typography>
              </Box>
              <Alert severity="info">
                Dry-run: the Sales Order payload was built and validated but <b>not</b> posted to Focus.
                Disable dry-run during go-live to create the real SO.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={close} variant="contained">Done</Button></DialogActions>
      </Dialog>

      {/* Error */}
      <Dialog open={stage === "error"} onClose={close}>
        <DialogTitle>Push failed</DialogTitle>
        <DialogContent sx={{ minWidth: 340 }}>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions><Button onClick={close}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
};

export default PushToFocusButton;
